import { parseCsv } from "./rosterCsv";

/**
 * An operator's customer file, read in the browser (owner 2026-11-01). The server
 * decides; this only maps columns. The file is never uploaded or kept: its rows
 * travel as JSON, and a password column is live cleartext.
 */
export const IMPORT_COLUMNS = ["username", "mobile", "name", "gender", "plan", "expiry", "password", "deviceid", "logins_allowed", "address", "city", "state", "pin"] as const;
export type ImportColumn = (typeof IMPORT_COLUMNS)[number];
const REQUIRED: ImportColumn[] = ["name", "plan", "expiry"];
/** What an old system tends to call a field. The operator can change any of them. */
const OTHER_NAMES: Partial<Record<ImportColumn, string[]>> = {
  username: ["user_id", "userid", "user_name", "login"],
  mobile: ["phone", "contact", "mobile_no", "mobile_number", "contact_no"],
  gender: ["sex"],
  name: ["customer_name", "full_name", "subscriber_name"],
  plan: ["plan_name"],
  expiry: ["expiry_date", "expire_date", "expires", "valid_till", "valid_upto"],
};
/** Our field to the file's column heading; a field left out is not in the file. */
export type ColumnMap = Partial<Record<ImportColumn, string>>;
/** Small enough that one request ends well inside the gateway's time limit. */
export const IMPORT_BATCH = 25;

export const IMPORT_TEMPLATE = `${IMPORT_COLUMNS.join(",")}\n,9876543210,Asha Rao,female,YOUR_PLAN_NAME,2026-12-31,,,,1 Main Road,Pune,Maharashtra,411001\n`;

export interface ImportRow {
  line: number;
  username: string;
  mobile: string;
  name: string;
  gender: string;
  plan: string;
  expiry: string;
  password: string;
  deviceid: string;
  logins_allowed: number;
  address: string;
  city: string;
  state: string;
  pin: string;
}

export interface ImportRead {
  rows: ImportRow[];
  problem?: string;
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/** To YYYY-MM-DD. Day first, as Indian files write it. What it cannot read goes to the server as it is, and is refused there. */
export function normalDate(s: string): string {
  const t = s.trim();
  const iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(t);
  const dmy = /^(\d{1,2})[-/.](\d{1,2}|[A-Za-z]{3,9})[-/.](\d{4}|\d{2})$/.exec(t);
  const out = iso ? [iso[1], iso[2], iso[3]] : dmy ? [dmy[3], dmy[2], dmy[1]] : null;
  if (!out) return t;
  const [y, m, d] = out as [string, string, string];
  const month = /^\d/.test(m) ? Number(m) : MONTHS.indexOf(m.slice(0, 3).toLowerCase()) + 1;
  if (month < 1 || month > 12) return t;
  return `${y.length === 2 ? `20${y}` : y}-${String(month).padStart(2, "0")}-${d.padStart(2, "0")}`;
}

/** 10 digits from +91, 91 or 0 spellings. Anything else is left for the server to refuse. */
export function normalMobile(s: string): string {
  const d = s.trim().replace(/[\s-]/g, "").replace(/^\+/, "");
  if (!/^\d+$/.test(d)) return s.trim();
  if (d.length === 12 && d.startsWith("91")) return d.slice(2);
  if (d.length === 11 && d.startsWith("0")) return d.slice(1);
  return d;
}

function normalGender(s: string): string {
  const g = s.trim().toLowerCase();
  return g === "m" ? "male" : g === "f" ? "female" : g;
}

/** Lines repeating a login earlier in the whole file, and lines sharing a mobile. The server sees one batch at a time. */
export function fileChecks(rows: ImportRow[]): { repeated: Map<number, string>; sharedMobile: Set<number> } {
  const logins = new Map<string, number>();
  const mobiles = new Map<string, number[]>();
  const repeated = new Map<number, string>();
  for (const r of rows) {
    if (r.mobile) mobiles.set(r.mobile, [...(mobiles.get(r.mobile) ?? []), r.line]);
    for (const login of [r.username || r.mobile, r.deviceid]) {
      if (!login) continue;
      const key = login.toLowerCase();
      const first = logins.get(key);
      if (first !== undefined && first !== r.line) repeated.set(r.line, `${login} is also on line ${first}`);
      else logins.set(key, r.line);
    }
  }
  return { repeated, sharedMobile: new Set([...mobiles.values()].filter((l) => l.length > 1).flat()) };
}

export function fileHeadings(text: string): string[] {
  return (parseCsv(text)[0]?.fields ?? []).map((h) => h.trim());
}

/** The first guess at the operator's column match, by name. */
export function guessColumns(headings: string[]): ColumnMap {
  const out: ColumnMap = {};
  for (const c of IMPORT_COLUMNS) {
    const names = [c, ...(OTHER_NAMES[c] ?? [])];
    const found = names.map((n) => headings.find((h) => h.toLowerCase() === n)).find(Boolean);
    if (found) out[c] = found;
  }
  return out;
}

export function readImportFile(text: string, map?: ColumnMap): ImportRead {
  const records = parseCsv(text);
  const headings = (records[0]?.fields ?? []).map((h) => h.trim());
  const columns = map ?? guessColumns(headings);
  const missing = REQUIRED.filter((c) => !columns[c]);
  if (missing.length) return { rows: [], problem: `Choose the column for ${missing.join(", ")}.` };
  if (!columns.username && !columns.mobile) return { rows: [], problem: "Choose the column for mobile or username." };
  const at = (fields: string[], c: ImportColumn) => {
    const i = columns[c] ? headings.indexOf(columns[c]) : -1;
    return i < 0 ? "" : (fields[i] ?? "").trim();
  };
  const rows = records.slice(1).filter((r) => r.fields.some((f) => f.trim())).map((r) => ({
    line: r.line,
    username: at(r.fields, "username"),
    mobile: normalMobile(at(r.fields, "mobile")),
    name: at(r.fields, "name"),
    gender: normalGender(at(r.fields, "gender")),
    plan: at(r.fields, "plan"),
    expiry: normalDate(at(r.fields, "expiry")),
    password: at(r.fields, "password"),
    deviceid: at(r.fields, "deviceid"),
    logins_allowed: Number(at(r.fields, "logins_allowed")) || 0,
    address: at(r.fields, "address"),
    city: at(r.fields, "city"),
    state: at(r.fields, "state"),
    pin: at(r.fields, "pin"),
  }));
  return rows.length ? { rows } : { rows, problem: "The file has no customers." };
}
